import { PaywallDisplay, retrievePaymentToken, setDisplayState } from 'redux/slices/paywall'
import React, { useCallback, useState, useMemo } from 'react'
import { getShow, setVideoOnDemand } from 'redux/slices/liveShow'
import { useDispatch, useSelector } from 'react-redux'

import CallToActionButton from 'components/common/CallToActionButton/CallToActionButton'
import { VideoItem } from 'components/set/VideoItem/VideoItem'
import logoImg from 'assets/default-recording-thumbnail.png'
import { isNil } from 'lodash'
import moment from 'moment'
import { openModal, ModalContent } from 'redux/slices/globalModal'
import useStyles from './styles'
import { userHasPurchasedTicket } from 'redux/slices/passes'
import { VideoOnDemandType } from 'data/Show'

const isVideoExpired = video => !isNil(video.expiresAt) && moment().isSameOrAfter(video.expiresAt)
const isVideoPaidContent = video => !isNil(video.paidVideo) && video.paidVideo
const isVideoLockedOrExpired = (video, userPurchasedTicket) =>
    (!userPurchasedTicket && isVideoPaidContent(video)) || isVideoExpired(video)

export const VideoList = () => {
    const classes = useStyles()
    const dispatch = useDispatch()

    const [buttonClicked, setButtonClicked] = useState(false)

    const { videosOnDemand, title, artist, willBeRecorded } = useSelector(getShow)
    const userPurchasedTicket = useSelector(userHasPurchasedTicket)

    const onUnlock = useCallback(async () => {
        if (!buttonClicked) {
            setButtonClicked(true)
            await dispatch(retrievePaymentToken())
            dispatch(setDisplayState(PaywallDisplay.PROMPT))
        }
    }, [dispatch, buttonClicked])

    const onVodClick = useCallback(
        video => {
            if (video.playbackId) {
                if (!isVideoLockedOrExpired(video, userPurchasedTicket)) {
                    dispatch(setVideoOnDemand(video.playbackId))
                } else {
                    onUnlock()
                }
            } else {
                dispatch(openModal({ content: ModalContent.FULL_RECORDING }))
            }
        },
        [dispatch, onUnlock, userPurchasedTicket],
    )
    const fullRecording = useMemo(() => videosOnDemand.find(vod => vod.type === VideoOnDemandType.FULL_RECORDING), [
        videosOnDemand,
    ])
    const highlightVideos = useMemo(() => videosOnDemand.filter(vod => vod.type === VideoOnDemandType.HIGHLIGHT), [
        videosOnDemand,
    ])

    const showCtaButton = useMemo(
        () =>
            (!userPurchasedTicket && willBeRecorded && fullRecording && !isVideoExpired(fullRecording)) ||
            (!userPurchasedTicket && highlightVideos.some(vod => vod.paidVideo && !isVideoExpired(vod))),
        [fullRecording, highlightVideos, userPurchasedTicket, willBeRecorded],
    )

    return (
        <div className={classes.wrapper}>
            <div>
                <h6 className={classes.header}>Full event Recording</h6>
                {!willBeRecorded && (
                    <VideoItem
                        id="fake-full-recording"
                        src={logoImg}
                        gif={logoImg}
                        title={`${artist.name} replay of ${title}`}
                        duration="1:00:00"
                        isLocked={false}
                        isExpired={false}
                        onClick={onVodClick}
                    />
                )}

                {willBeRecorded && fullRecording && (
                    <VideoItem
                        id="full-recording"
                        video={fullRecording}
                        isLocked={isVideoLockedOrExpired(fullRecording, userPurchasedTicket)}
                        isExpired={isVideoExpired(fullRecording)}
                        onClick={onVodClick}
                    />
                )}

                {willBeRecorded && !fullRecording && (
                    <p className={classes.title} data-testid="full-recording-msg">
                        Check back soon for the full recording
                    </p>
                )}
            </div>
            <div>
                <h6 className={classes.header}>Show Highlights</h6>
                {highlightVideos.map(highlight => {
                    return (
                        <VideoItem
                            key={highlight.playbackId}
                            id={`highlight-${highlight.playbackId}`}
                            video={highlight}
                            onClick={onVodClick}
                            isLocked={isVideoLockedOrExpired(highlight, userPurchasedTicket)}
                            isExpired={isVideoExpired(highlight)}
                        />
                    )
                })}

                {!highlightVideos.length && (
                    <p className={classes.title} data-testid="no-highlights-msg">
                        Check back soon for all the highlights
                    </p>
                )}
            </div>
            {showCtaButton && (
                <div className={classes.buttonWrapper}>
                    <CallToActionButton
                        testId="purchase-ticket-btn"
                        type="secondary"
                        text={!buttonClicked ? 'Unlock videos' : 'Redirecting...'}
                        onClick={onUnlock}
                        className={classes.submitBtn}
                        display="block"
                    />
                </div>
            )}
        </div>
    )
}

export default React.memo(VideoList)
